import { getDashboardWidgetConfigurationDetails } from 'apis/dashboardApis'
import { Rules } from 'common/rules'
import { SlideShow } from 'components/SlideShow/SlideShow'
import { useDtmAnalytics } from 'hooks/useDtmAnalytics'
import { useFetch } from 'hooks/useFetch'
import { Layout } from 'layouts/Layout'
import { AvailableInventory } from 'modules/AvailableInventory'
import { BuyerActivity } from 'modules/BuyerActivity'
import { DefaultErrorBoundary } from 'modules/DefaultErrorBoundary'
import { GroundingOverview } from 'modules/GroundingOverview/GroundingOverview'
import { MyActivityYTD } from 'modules/MyActivityYTD/MyActivityYTD'
import { SearchGeneral } from 'modules/SearchGeneral/SearchGeneral'
import { PropsWithChildren, useEffect, useLayoutEffect, useState } from 'react'
import { RouteProps, useLocation } from 'react-router-dom'
import { useDashboardStore } from 'store/useDashboardStore'
import { useGlobalStore } from 'store/useGlobalStore'
import { setListingBreadcrumb } from 'utils/breadcrumbUtils'
import shallow from 'zustand/shallow'
import { BUYER_TYPE_ID, SessionStorageKey } from '../common/constants'
import { StyledDashboardPlaceholder, StyledDashboardSlide } from './Dashboard.styled'

interface IAuthenticatedRouteProps extends PropsWithChildren<RouteProps> {
  allowedRules?: Rules[]
}

const AuthorizedComponent = ({ allowedRules, children }: IAuthenticatedRouteProps) => {
  const isInRule = useGlobalStore((state) => state.isInRule, shallow)

  return <div className="page-content">{allowedRules?.some((rule) => isInRule(rule)) && children}</div>
}

export const Dashboard = () => {
  const { endPageLoad } = useDtmAnalytics()
  const { fetchGroundingData, fetchFilters } = useDashboardStore((state) => state, shallow)
  const { loading: filterLoading } = useFetch(() => fetchFilters())
  const { loading: groundingDataLoading } = useFetch(() => fetchGroundingData())
  const [buyerID] = useGlobalStore((state) => [state.userClaims.CurrentBuyerID])

  const {
    userClaims: { DealershipName: dealerShipName, AssociatedBuyers: associatedBuyers }
  } = useGlobalStore(
    (state) => ({
      userClaims: state.userClaims
    }),
    shallow
  )
  const [showSearch, setShowSearch] = useState(true)

  const location = useLocation()
  const { loading: widgetLoading, data: widgetData } = useFetch(() => getDashboardWidgetConfigurationDetails())

  useLayoutEffect(() => {
    window.sessionStorage.removeItem(SessionStorageKey.SEARCH_FILTERS)
    window.sessionStorage.removeItem(SessionStorageKey.VEHICLE_INSTANCES)
    window.sessionStorage.removeItem(SessionStorageKey.GROUND_VIN)
    setListingBreadcrumb('Dashboard', location.pathname)
  }, [])

  useEffect(() => {
    const dealer = associatedBuyers?.find((item) => item.Description === dealerShipName)
    if (
      dealer.BuyerTypeID === BUYER_TYPE_ID.CORPORATE_OFFICE_UPSTREAM ||
      dealer.BuyerTypeID === BUYER_TYPE_ID.CORPORATE_OFFICE_NON_UPSTREAM
    ) {
      setShowSearch(false)
    }
  }, [])

  useEffect(() => {
    if (!filterLoading) {
      endPageLoad()
    }
  }, [filterLoading, endPageLoad])

  return (
    <Layout title="Dashboard">
      <StyledDashboardPlaceholder>
        <DefaultErrorBoundary>
          <StyledDashboardSlide>
            <SlideShow />
          </StyledDashboardSlide>
        </DefaultErrorBoundary>
        <AuthorizedComponent allowedRules={[Rules.PAGE_RDP_SRP, Rules.VIEW_ADVANCED_SEARCH_PAGE]}>
          {showSearch && (
            <DefaultErrorBoundary>
              <SearchGeneral isFilterLoading={filterLoading} />
            </DefaultErrorBoundary>
          )}
        </AuthorizedComponent>
        <AuthorizedComponent allowedRules={[Rules.VIEW_GROUNDING_QUICK_START, Rules.VIEW_GROUNDING_ACTIVITY]}>
          <DefaultErrorBoundary>
            <GroundingOverview loading={groundingDataLoading} widgetLoading={widgetLoading} widgetData={widgetData} />
          </DefaultErrorBoundary>
        </AuthorizedComponent>
        <AuthorizedComponent allowedRules={[Rules.PAGE_RDP_SRP, Rules.VIEW_ADVANCED_SEARCH_PAGE]}>
          <DefaultErrorBoundary>
            <AvailableInventory isFilterLoading={filterLoading} />
          </DefaultErrorBoundary>
          <DefaultErrorBoundary>
            <BuyerActivity />
          </DefaultErrorBoundary>
        </AuthorizedComponent>
        <AuthorizedComponent allowedRules={[Rules.VIEW_GROUNDING_ACTIVITY]}>
          <DefaultErrorBoundary>
            <MyActivityYTD loading={groundingDataLoading} />
          </DefaultErrorBoundary>
        </AuthorizedComponent>
      </StyledDashboardPlaceholder>
    </Layout>
  )
}
